import { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Select, Pagination } from '../../components/ui';
import { apiFetch } from '../../services/api';

export default function CertificatesListPage() {
  const [certs, setCerts]       = useState([]);
  const [cohorts, setCohorts]   = useState([]);
  const [programs, setPrograms] = useState([]);
  const [pagination, setPag]    = useState(null);
  const [loading, setLoading]   = useState(true);
  const [page, setPage]         = useState(1);
  const [filter, setFilter]     = useState({ cohort_id: '', program_id: '' });
  const [selectedRows, setSelected] = useState(new Set());
  const [revokeModal, setRevokeModal] = useState(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [dlFormat, setDlFormat]   = useState('zip');
  const [dlModal, setDlModal]     = useState(false);
  const [saving, setSaving]       = useState(false);

  const token = localStorage.getItem('mh_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30 });
      if (filter.cohort_id)  params.set('cohort_id', filter.cohort_id);
      if (filter.program_id) params.set('program_id', filter.program_id);
      const res = await apiFetch(`/admin/certificates?${params}`, { headers });
      const data = await res.json();
      setCerts(data.data || []); setPag(data.pagination);
    } catch { setCerts([]); } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    apiFetch('/programs/cohorts', { headers }).then(r => r.json()).then(d => setCohorts(d.data || []));
    apiFetch('/programs?limit=100', { headers }).then(r => r.json()).then(d => setPrograms(d.data || []));
  }, []);

  const handleRevoke = async () => {
    setSaving(true);
    try {
      await apiFetch(`/admin/certificates/${revokeModal.id}/revoke`, {
        method: 'PUT', headers, body: JSON.stringify({ reason: revokeReason }),
      });
      setRevokeModal(null); setRevokeReason(''); load();
    } catch { alert('Revoke failed'); } finally { setSaving(false); }
  };

  const handleBulkDownload = async () => {
    if (!selectedRows.size) { alert('Select certificates to download'); return; }
    setDownloading(true);
    try {
      const selectedCerts = certs.filter(c => selectedRows.has(c.id));
      const { downloadBulkCertificates } = await import('../../components/CertificateTemplate');
      await downloadBulkCertificates(selectedCerts, dlFormat);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally { setDownloading(false); setDlModal(false); }
  };

  const toggleRow = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleAll = () => {
    setSelected(selectedRows.size === certs.length ? new Set() : new Set(certs.map(c => c.id)));
  };

  const COLS = [
    { key: '_select', label: (
      <input type="checkbox" checked={selectedRows.size === certs.length && certs.length > 0} onChange={toggleAll} style={{ cursor: 'pointer' }} />
    ), render: (_, row) => (
      <input type="checkbox" checked={selectedRows.has(row.id)} onChange={() => toggleRow(row.id)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer' }} />
    )},
    { key: 'recipient_name', label: 'Recipient', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.recipient_email}</div>
      </div>
    )},
    { key: 'program_title', label: 'Program', render: (v, row) => (
      <div>
        <div style={{ fontSize: 13 }}>{v || '—'}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.cohort_name || ''}</div>
      </div>
    )},
    { key: 'title_text', label: 'Type', render: v => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{v || 'Certificate of Completion'}</span> },
    { key: 'template_name', label: 'Template', render: v => <span style={{ fontSize: 11, color: 'var(--accent)' }}>{v || 'Default'}</span> },
    { key: 'cert_hash', label: 'Hash', render: v => <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--text-muted)' }}>{v?.slice(0,14)}…</span> },
    { key: 'issued_at', label: 'Issued', render: v => <span style={{ fontSize: 11 }}>{new Date(v).toLocaleDateString()}</span> },
    { key: 'is_revoked', label: 'Status', render: v => v
      ? <span style={{ fontSize: 11, color: 'var(--red-light)', fontWeight: 600 }}>Revoked</span>
      : <span style={{ fontSize: 11, color: 'var(--green-light)' }}>Valid</span>
    },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => window.open(`/verify/${row.cert_hash}`, '_blank')}>View</Button>
        {!row.is_revoked && <Button variant="ghost" size="sm" style={{ color: 'var(--red-light)' }} onClick={() => { setRevokeModal(row); setRevokeReason(''); }}>Revoke</Button>}
      </div>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>Issued Certificates</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pagination?.total ?? 0} certificates issued</div>
        </div>
        {selectedRows.size > 0 && (
          <Button onClick={() => setDlModal(true)} loading={downloading}>
            ⬇ Download {selectedRows.size} Certificate{selectedRows.size > 1 ? 's' : ''}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10 }}>
        <Select value={filter.program_id} onChange={e => setFilter(f => ({ ...f, program_id: e.target.value }))} style={{ width: 220 }}>
          <option value="">All Programs</option>
          {programs.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
        </Select>
        <Select value={filter.cohort_id} onChange={e => setFilter(f => ({ ...f, cohort_id: e.target.value }))} style={{ width: 220 }}>
          <option value="">All Cohorts</option>
          {cohorts.map(c => <option key={c.id} value={c.id}>{c.program_title} — {c.cohort_name}</option>)}
        </Select>
      </div>

      {selectedRows.size > 0 && (
        <div style={{ padding: '10px 16px', background: 'rgba(232,164,58,0.08)', border: '1px solid rgba(232,164,58,0.25)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
          <span style={{ color: 'var(--accent)' }}>{selectedRows.size} selected</span>
          <button onClick={() => setSelected(new Set())} style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Clear</button>
        </div>
      )}

      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <Table columns={COLS} data={certs} loading={loading} />
        <Pagination pagination={pagination} onPage={setPage} />
      </div>

      {/* Download format modal */}
      <Modal open={dlModal} onClose={() => setDlModal(false)} title={`Download ${selectedRows.size} Certificates`} width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {[
              { value: 'zip',    label: 'ZIP of PDFs',       desc: 'One PDF per student, zipped together', icon: '◈' },
              { value: 'merged', label: 'Single PDF',        desc: 'All certificates in one PDF file',      icon: '◎' },
            ].map(opt => (
              <div key={opt.value} onClick={() => setDlFormat(opt.value)} style={{ padding: '16px', borderRadius: 10, border: `2px solid ${dlFormat === opt.value ? 'var(--accent)' : 'var(--border)'}`, background: dlFormat === opt.value ? 'var(--accent-muted)' : 'var(--bg-elevated)', cursor: 'pointer', transition: 'all 0.15s' }}>
                <div style={{ fontSize: 20, marginBottom: 8, color: dlFormat === opt.value ? 'var(--accent)' : 'var(--text-muted)' }}>{opt.icon}</div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>{opt.desc}</div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Each certificate will be generated as a landscape A4 PDF using the template settings stored at the time of issuance.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setDlModal(false)}>Cancel</Button>
            <Button onClick={handleBulkDownload} loading={downloading}>
              {downloading ? 'Generating…' : `Download as ${dlFormat === 'zip' ? 'ZIP' : 'PDF'}`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Revoke modal */}
      <Modal open={!!revokeModal} onClose={() => setRevokeModal(null)} title={`Revoke Certificate — ${revokeModal?.recipient_name}`} width={440}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: '12px 16px', background: 'rgba(240,96,96,0.08)', border: '1px solid rgba(240,96,96,0.3)', borderRadius: 8, fontSize: 13, color: 'var(--red-light)', lineHeight: 1.6 }}>
            Revoking a certificate marks it as invalid. Anyone scanning the QR code will see it has been revoked. This cannot be undone.
          </div>
          <div>
            <label style={labelStyle}>Reason for Revocation</label>
            <textarea value={revokeReason} onChange={e => setRevokeReason(e.target.value)} rows={3} placeholder="e.g. Issued in error, Academic misconduct…"
              style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setRevokeModal(null)}>Cancel</Button>
            <Button onClick={handleRevoke} loading={saving} style={{ background: 'var(--red)', color: '#fff' }}>Revoke Certificate</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'block', marginBottom: 6 };
